// Operator session API — the REST login endpoint that mints the Bearer JWT the SPA then
// attaches to every GraphQL request.
//
// `POST /api/session` with { email, password }:
//   200 -> { token, operator: { id, email, role } }
//   401 -> { error: { code, message } }   (bad credentials)
//   400 -> { error: { code, message } }   (malformed request)
//
// The password is only ever sent in this request body and is never stored or logged.

/** Endpoint for operator login. Relative so it works behind any host/proxy. */
export const SESSION_ENDPOINT = '/api/session';

/** The authenticated operator returned alongside the token. */
export interface Operator {
  id: string;
  email: string;
  role: string;
}

export interface LoginResponse {
  token: string;
  operator: Operator;
}

interface ApiErrorBody {
  error?: { code?: string; message?: string };
}

/** Error thrown for a failed login. `status` distinguishes 401 (bad creds) from 400/5xx. */
export class LoginError extends Error {
  readonly status: number | undefined;
  readonly code: string | undefined;

  constructor(message: string, options: { status?: number; code?: string } = {}) {
    super(message);
    this.name = 'LoginError';
    this.status = options.status;
    this.code = options.code;
  }
}

export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Authenticate an operator. Resolves with the token + operator on success; throws
 * {@link LoginError} (with the HTTP status preserved) on failure.
 */
export async function login({
  email,
  password,
}: LoginCredentials): Promise<LoginResponse> {
  let response: Response;
  try {
    response = await fetch(SESSION_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch (cause: unknown) {
    throw new LoginError(
      cause instanceof Error ? cause.message : 'Network request failed',
    );
  }

  let body: (Partial<LoginResponse> & ApiErrorBody) | null = null;
  try {
    body = (await response.json()) as Partial<LoginResponse> & ApiErrorBody;
  } catch {
    body = null;
  }

  if (!response.ok) {
    const message =
      response.status === 401
        ? (body?.error?.message ?? 'Invalid email or password')
        : (body?.error?.message ?? `Login failed (${response.status})`);
    throw new LoginError(message, {
      status: response.status,
      ...(body?.error?.code ? { code: body.error.code } : {}),
    });
  }

  if (!body?.token || !body.operator) {
    throw new LoginError('Login response was malformed', { status: response.status });
  }

  return { token: body.token, operator: body.operator };
}
